export type DraftKind = 'work' | 'project' | 'client'

export interface LocalDraft<T = unknown> {
  id: string
  kind: DraftKind
  userId: string
  workspaceId: string
  title: string
  updatedAt: string
  payload: T
}

const PREFIX = 'tapwork:saved-draft:v1:'
export const DRAFTS_CHANGED_EVENT = 'tapwork:drafts-changed'

function announceDraftChange(): void {
  try {
    window.dispatchEvent(new Event(DRAFTS_CHANGED_EVENT))
  } catch {
    // SSR/tests without a browser do not need a navigation refresh.
  }
}

function storageKey(draft: Pick<LocalDraft, 'userId' | 'workspaceId' | 'kind' | 'id'>): string {
  return `${PREFIX}${draft.userId}:${draft.workspaceId}:${draft.kind}:${draft.id}`
}

export function newDraftId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function saveLocalDraft<T>(draft: LocalDraft<T>): void {
  try {
    localStorage.setItem(storageKey(draft), JSON.stringify(draft))
    announceDraftChange()
  } catch {
    // Draft saving is a convenience. A blocked/full browser store must never break a form.
  }
}

export function deleteLocalDraft(draft: Pick<LocalDraft, 'userId' | 'workspaceId' | 'kind' | 'id'>): void {
  try {
    localStorage.removeItem(storageKey(draft))
    announceDraftChange()
  } catch {
    // Nothing else to do when browser storage is unavailable.
  }
}

export function listLocalDrafts(userId: string, workspaceId: string): LocalDraft[] {
  const prefix = `${PREFIX}${userId}:${workspaceId}:`
  const drafts: LocalDraft[] = []
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index)
      if (!key?.startsWith(prefix)) continue
      const raw = localStorage.getItem(key)
      if (!raw) continue
      const parsed = JSON.parse(raw) as LocalDraft
      if (parsed?.userId === userId && parsed?.workspaceId === workspaceId && parsed.id && parsed.kind) drafts.push(parsed)
    }
  } catch {
    return []
  }
  return drafts.sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
}

export function getLocalDraft<T>(userId: string, workspaceId: string, kind: DraftKind, id: string): LocalDraft<T> | null {
  try {
    const raw = localStorage.getItem(`${PREFIX}${userId}:${workspaceId}:${kind}:${id}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LocalDraft<T>
    return parsed?.userId === userId && parsed?.workspaceId === workspaceId ? parsed : null
  } catch {
    return null
  }
}
